/**
 * THE ONLY SOURCE OF CONTEXT. 09 §3.4.
 *
 *   "Company and period are selected in the context bar and nowhere else."
 *
 * That sentence is the most-cited UI rule in the accepted architecture, and Gate A explains why:
 * 70 distinct authentication expressions, 12 company-guard expressions and company resolution
 * differing per module (`?db` in FS/bank/grants, `_Cc2Slug` in cc2, `Test-FinsiteVisibleToUser` in
 * finsite). A frontend grown over that surface grows a company dropdown per module. This file exists
 * so that cannot happen again.
 *
 * The API is split in two on purpose:
 *
 *   useWorkContext()      — READ. Any screen may ask what it is looking at (it needs the companyUid
 *                           to fetch). Read-only: there is no setter on the returned object.
 *   useContextSelection() — CHOOSE. Only <ContextBar> may call it, and the parameter type admits
 *                           exactly one caller so a second call site fails to compile.
 *
 * Nothing here decides permission. `visibleCompanies` and `modules` are the server's answers,
 * rendered as given (09 §5.1 / CONFLICT C-1).
 */

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type {
  CompanySummary,
  ModuleEntitlement,
  PeriodSummary,
  SessionContext,
  SessionSummary,
  TenantSummary,
} from '../api/contract';
import type { ServerData } from '../api/useServerData';
import { readChosenCompany, writeChosenCompany } from './chosenCompany';

/** 09 §3.1 — the six slots of the bar, in RTL reading order: הארגון ‹ החברה ‹ המודול ‹ הישות ‹ התקופה. */
export interface WorkContext {
  readonly session: SessionSummary | null;
  /** PLATFORM and TENANT never bleed and are never simultaneous (04 I6). */
  /**
   * `null` until `auth.whoami.read` answers. CM-014: there is no default, because a default is a
   * surface somebody was granted without a session saying so — and TENANT was the old one.
   */
  readonly surface: SessionContext | null;
  readonly tenant: TenantSummary | null;
  readonly company: CompanySummary | null;
  readonly module: ModuleEntitlement | null;
  /** 09 §3.1 "הישות" — set by a child screen bound to exactly one row. */
  readonly entityLabelHe: string | null;
  readonly period: PeriodSummary | null;

  /** The server's answers, rendered as given. Never filtered client-side. */
  readonly visibleCompanies: readonly CompanySummary[];
  /** The read `visibleCompanies` was derived from. See `ContextProviderProps.companiesResult`. */
  readonly companiesResult: ServerData<{ readonly companies: readonly CompanySummary[] }>;
  readonly modules: readonly ModuleEntitlement[];
  readonly periods: readonly PeriodSummary[];
  /**
   * RE-READ THE SESSION. **2026-10-26.**
   *
   * The header, the office picker and the home page all name the office from
   * `auth.whoami.read`. A screen that CHANGES something the session reports — today the office
   * name — must be able to ask for that read again, or the product tells the person their change
   * will appear after they sign in again. The Owner is explicit that this is not acceptable:
   * *"הסבר שההתחברות מחזיקה שם ישן אינו תחליף לעדכון התצוגה"*.
   *
   * It is a REQUEST for a re-read, not a setter: no screen may write the session, and nothing here
   * decides what the session says. `<App>` owns it and the server answers it.
   */
  readonly refreshSession: () => Promise<void>;
}

/**
 * The selection API. Note what is NOT here: nothing that grants, nothing that filters a company
 * list by a client-side rule. Selecting rewrites the address; the gate re-decides on the next call.
 */
export interface ContextSelection {
  selectTenant(tenantUid: string): void;
  selectCompany(companyUid: string): void;
  selectPeriod(periodUid: string | null): void;
}

/*
 * WHAT IS NOT IN `ContextSelection`, AND WHY — **CM-014.**
 *
 * There was a `switchSurface(surface)` here, and a button in the context bar that called it. It set
 * a piece of React state, so a person on the tenant surface could put themselves on the platform
 * surface by clicking, and the shell believed them. The address did the same thing independently:
 * `/platform` minted a PLATFORM session, so typing a URL granted a surface.
 *
 * OA-0001 §2.1 withdrew the two-context identity outright — an account's context comes from
 * `app_user.account_kind` at authentication and CANNOT change. So the surface is not state here,
 * it is a READ of `session.sessionContext`, which comes from `auth.whoami.read` and therefore from
 * the server. There is no setter to call, no flag to set and no address that grants anything.
 *
 * A platform operator who needs to be inside a customer's data does it the one way the model has: a
 * support grant, requested, approved by a second principal, consented to where the customer requires
 * it, time-boxed and audited on every call. That is a different mechanism, not a faster version of
 * this one.
 */

/**
 * The single admissible caller. Widening this union re-opens the defect the rule exists to prevent,
 * so widening it is the change a reviewer must refuse.
 */
export type ContextSelectorCaller = 'ContextBar';

interface ContextStore extends WorkContext {
  readonly selection: ContextSelection;
  setEntityLabel(label: string | null): void;
  setModuleByCode(moduleCode: string | null): void;
  setCompanyByUid(companyUid: string | null): void;
}

const StoreContext = createContext<ContextStore | null>(null);

function useStore(): ContextStore {
  const store = useContext(StoreContext);
  if (store === null) {
    throw new Error('Rendered outside <ContextProvider>. Context has exactly one source.');
  }
  return store;
}

/** READ. Available to every screen; carries no setter. */
export function useWorkContext(): WorkContext {
  return useStore();
}

/**
 * CHOOSE. 09 §3.4 — the context bar and nowhere else.
 *
 * The `caller` argument is not ceremony: `ContextSelectorCaller` is a single-member union, so a
 * module that tries to grow its own company dropdown fails at compile time, and the dev-time throw
 * below catches a cast. `src/shell/contextSingleSource.test.ts` additionally scans the source tree.
 */
export function useContextSelection(caller: ContextSelectorCaller): ContextSelection {
  const store = useStore();
  if (import.meta.env.DEV && caller !== 'ContextBar') {
    throw new Error(
      '09 §3.4: company and period are selected in the context bar and nowhere else. ' +
        'No module ever gets its own company dropdown again.',
    );
  }
  return store.selection;
}

/**
 * Set by a screen to fill the המודול / הישות slots of the bar. This is the bar READING the current
 * screen, which is the opposite direction from choosing — the screen never chooses a company here.
 */
export function useDeclareScreenContext(): {
  setEntityLabel: (label: string | null) => void;
  setModuleByCode: (moduleCode: string | null) => void;
  setCompanyByUid: (companyUid: string | null) => void;
} {
  const store = useStore();
  return {
    setEntityLabel: store.setEntityLabel,
    setModuleByCode: store.setModuleByCode,
    setCompanyByUid: store.setCompanyByUid,
  };
}

export interface ContextProviderProps {
  readonly session: SessionSummary | null;
  readonly companies: readonly CompanySummary[];
  /**
   * The READ that produced `companies`, when there was one. **Phase 2 §6, vertical A.**
   *
   * `visibleCompanies` is a list, and a list cannot say whether it is empty because the office has
   * no clients or because the read was refused. A screen that must tell those apart — the picker,
   * whose entire subject is "which companies may I open" — renders this through `<ServerRead>`;
   * every other consumer keeps using `visibleCompanies` and is unaffected.
   *
   * There is still ONE SOURCE: the list is DERIVED from this result by whoever passes both, and a
   * provider that let them disagree would be the two-answers problem 04 I6 exists to prevent. It is
   * optional so that the many tests which supply a plain array do not have to state a read that
   * never happened.
   */
  readonly companiesResult?: ServerData<{ readonly companies: readonly CompanySummary[] }>;
  readonly modules: readonly ModuleEntitlement[];
  readonly periods: readonly PeriodSummary[];
  /** Injected by <App> so this file has no router dependency and stays unit-testable. */
  readonly navigate: (path: string) => void;
  /**
   * Injected by <App>, which owns the session. Optional so the many tests that supply a fixed
   * session need not state a re-read that cannot happen; absent, it resolves and changes nothing,
   * which is exactly what a fixed session means.
   */
  readonly refreshSession?: () => Promise<void>;
  readonly children: ReactNode;
}

export function ContextProvider(props: ContextProviderProps): JSX.Element {
  const { session, companies, companiesResult, modules, periods, navigate, refreshSession, children } = props;

  // NOT STATE. CM-014: the surface is whatever the server said the session is, and until the
  // server has answered there is no surface — `null` renders the signed-out shell rather than
  // guessing TENANT, because a guessed surface is a surface somebody was granted by a default.
  const surface: SessionContext | null = session?.sessionContext ?? null;
  /*
   * ── THE ACTIVE OFFICE FOLLOWS THE SESSION. **Section 2, 2026-10-27.** ─────────────────────────
   *
   * This was `useState(session?.activeTenantUid ?? null)`, and that is the whole of the Owner's
   * section 2: *"בהגדרות מופיע השם החדש, אך בדף הבית ובכותרת הוא אינו מופיע."*
   *
   * **A `useState` initialiser runs on the FIRST render only.** The shell's first render has no
   * session -- `auth.whoami.read` is still in flight, and every arrival in this product is a full
   * page load -- so the uid was initialised to `null`, the session answered a tick later, and
   * nothing ever set it. `tenant` below is `find(t => t.tenantUid === tenantUid)`, so it stayed
   * `null` for the life of the page: no office name in the header, none on the home page, and the
   * office picker slot absent. For EVERY office, branded or not; the saved name was a red herring.
   *
   * It is DERIVED now, with an explicit choice able to override it:
   *
   *   `chosenTenantUid`  what somebody picked in the office slot, or `null` for "nobody has".
   *   `session.activeTenantUid`  the office THIS SESSION IS BOUND TO -- the server's own answer,
   *                              and the authority: *"The ACTIVE tenant is the one bound to this
   *                              session, never one chosen from the list"* (session-identity.ts).
   *
   * Derived rather than synchronised in an effect: an effect would reintroduce the same class of
   * bug one render later, and there is no state here that the session cannot answer for.
   */
  const [chosenTenantUid, setChosenTenantUid] = useState<string | null>(null);
  const tenantUid = chosenTenantUid ?? session?.activeTenantUid ?? null;
  /*
   * ── THE COMPANY FOLLOWS THE CHOICE, THE GRANT AND THE COUNT. **Owner section 3, 2026-10-28.** ─
   *
   *     *"בחירת חברה תשמור את דף הבית ותעדכן את הקשר הנתונים הרלוונטי … החברה הנבחרת
   *     תישמר במעבר בין מסכים וברענון, כל עוד ההרשאה תקפה … כשיש למשתמש חברה נגישה אחת בלבד, היא
   *     תיבחר אוטומטית."*
   *
   * This was `useState<string | null>(null)`, and it was the same class of defect as the office uid
   * above — only worse, because **every navigation in this shell is a plain `<a href>`**, so the
   * provider is destroyed and rebuilt on each one. Nothing cleared the picker; the picker was
   * rendering a new store. See `chosenCompany.ts`.
   *
   * Three inputs, evaluated in one order:
   *
   *   1. **What this person chose**, remembered across the page load — but only if it is STILL in
   *      `companies`, which is the server's grant-filtered `companies.list.read` answer. A revoked
   *      grant therefore drops out by itself, which is the whole of *"כל עוד ההרשאה תקפה"*.
   *   2. **One accessible company is not a choice**, so it needs no picker and is selected here.
   *   3. Otherwise nothing is selected and the bar asks.
   *
   * DERIVED, not synchronised in an effect, for the reason stated above `tenantUid`: the companies
   * arrive a tick after the first render, and an effect would reintroduce the same bug one render
   * later. Nothing is stored that the server's list cannot be asked about.
   */
  const [chosenCompanyUid, setChosenCompanyUid] = useState<string | null>(() => readChosenCompany());
  const companyUid = useMemo(() => {
    if (companies.some((c) => c.companyUid === chosenCompanyUid)) return chosenCompanyUid;
    return companies.length === 1 ? (companies[0]?.companyUid ?? null) : null;
  }, [companies, chosenCompanyUid]);

  /** Choosing one is a preference, so it is written where a page load cannot reach it. */
  const chooseCompany = useCallback((nextCompanyUid: string | null) => {
    setChosenCompanyUid(nextCompanyUid);
    writeChosenCompany(nextCompanyUid);
  }, []);
  const [moduleCode, setModuleCode] = useState<string | null>(null);
  const [periodUid, setPeriodUid] = useState<string | null>(null);
  const [entityLabelHe, setEntityLabelHe] = useState<string | null>(null);

  const tenant = useMemo(
    () => session?.tenants.find((t) => t.tenantUid === tenantUid) ?? null,
    [session, tenantUid],
  );
  const company = useMemo(
    () => companies.find((c) => c.companyUid === companyUid) ?? null,
    [companies, companyUid],
  );
  const moduleEntitlement = useMemo(
    () => modules.find((m) => m.moduleCode === moduleCode) ?? null,
    [modules, moduleCode],
  );
  const period = useMemo(
    () => periods.find((p) => p.periodUid === periodUid) ?? null,
    [periods, periodUid],
  );

  const selectCompany = useCallback(
    (nextCompanyUid: string) => {
      chooseCompany(nextCompanyUid);
      /*
       * ── WHERE THE PERSON ENDS UP, AND THE THIRD ARM IS THE FIX ────────────────────────
       *
       * 09 §5.1 *"On switch"*: land on the same module for the new company where that applies,
       * otherwise on the new company's home — never a blank screen and never an error.
       *
       * **That rule assumed the person was already inside a company.** Read on the home page it
       * said "go to `/c/<uid>`", which is the module list, where two modules are active and the
       * rest render as planned — *"החליפה את דף הבית במסך כמעט ריק"*. A company is CONTEXT,
       * not a destination (09 §3.4), so choosing one from a screen that is not a company screen
       * updates the context and moves nobody.
       *
       * `window.location.pathname` rather than the router's location, matching `AppShell`: this
       * provider is mounted above the routes and a full page load is how arrivals happen here.
       */
      const stillApplies =
        moduleCode !== null && modules.some((m) => m.moduleCode === moduleCode && m.available);
      const insideACompany =
        typeof window !== 'undefined' && window.location.pathname.startsWith('/c/');
      if (!stillApplies && !insideACompany) return;
      const target = stillApplies
        ? `/c/${nextCompanyUid}/m/${moduleCode ?? ''}`
        : `/c/${nextCompanyUid}`;
      navigate(periodUid === null ? target : `${target}?period=${periodUid}`);
    },
    [chooseCompany, moduleCode, modules, navigate, periodUid],
  );

  const selection = useMemo<ContextSelection>(
    () => ({
      selectTenant: (nextTenantUid: string) => {
        setChosenTenantUid(nextTenantUid);
        // A tenant switch invalidates every narrower slot. Nothing carries over — and the
        // remembered company is forgotten too, because it belongs to the office being left.
        chooseCompany(null);
        setModuleCode(null);
        setPeriodUid(null);
        setEntityLabelHe(null);
        navigate('/companies');
      },
      selectCompany,
      selectPeriod: (nextPeriodUid: string | null) => setPeriodUid(nextPeriodUid),
    }),
    [chooseCompany, navigate, selectCompany],
  );

  const store = useMemo<ContextStore>(
    () => ({
      session,
      surface,
      tenant,
      company,
      module: moduleEntitlement,
      entityLabelHe,
      period,
      visibleCompanies: companies,
      // Absent means "nobody read anything" — which is what a test supplying a fixed array is
      // saying, and it is `loaded` rather than `loading` so no screen sits on a skeleton forever.
      companiesResult: companiesResult ?? { state: 'loaded', data: { companies } },
      // Absent ⇒ the session is fixed and there is nothing to re-read. It RESOLVES rather than
      // throwing: a screen asking for a refresh it cannot get has still done the right thing.
      refreshSession: refreshSession ?? (async () => undefined),
      modules,
      periods,
      selection,
      setEntityLabel: setEntityLabelHe,
      setModuleByCode: setModuleCode,
      // A screen that DECLARES the company it is showing is stating the same fact the picker
      // states, so it is remembered the same way: arriving at `/c/<uid>` by link or by typed
      // address leaves the bar — and the next page — pointing at that company.
      setCompanyByUid: chooseCompany,
    }),
    [
      session,
      surface,
      tenant,
      company,
      moduleEntitlement,
      entityLabelHe,
      period,
      companies,
      companiesResult,
      modules,
      periods,
      selection,
    ],
  );

  return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
}
